import type { OrgCapability, OrgMemberRole } from '@shopgnd/shared';
import type { VendorPlanFeature } from '@/lib/vendorPlanGates';
import { proHub, vendorHub } from '@/lib/vendorHubPaths';

export type HubNavIconId =
  | 'layout-grid'
  | 'bar-chart'
  | 'scroll-text'
  | 'shopping-bag'
  | 'wallet'
  | 'package'
  | 'building'
  | 'tag'
  | 'credit-card'
  | 'store'
  | 'clipboard-check'
  | 'wrench'
  | 'inbox'
  | 'users'
  | 'star'
  | 'shield'
  | 'life-buoy'
  | 'messages'
  | 'megaphone'
  | 'file-text'
  | 'sun'
  | 'map-pin'
  | 'link'
  | 'folder-tree'
  | 'settings'
  | 'coins'
  | 'gift'
  | 'user-plus'
  | 'shield-check';

export const ROLE_RANK: Record<OrgMemberRole, number> = {
  owner: 4,
  manager: 3,
  staff: 2,
  viewer: 1,
};

export function roleAtLeast(role: OrgMemberRole, min: OrgMemberRole): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[min];
}

export type VendorSectionId =
  | 'home'
  | 'products'
  | 'orders'
  | 'bookings'
  | 'listings'
  | 'money'
  | 'store'
  | 'team'
  | 'profile';

export type VendorSectionTab = {
  id: string;
  label: string;
  to: string;
  capability?: OrgCapability | null;
  minRole?: OrgMemberRole;
  planFeature?: VendorPlanFeature;
  locked?: boolean;
};

export type VendorSectionConfig = {
  id: VendorSectionId;
  label: string;
  icon: HubNavIconId;
  to: string;
  capability?: OrgCapability | null;
  minRole?: OrgMemberRole;
  tabs?: VendorSectionTab[];
  /** Which hub route prefix this section uses when rendered. */
  surface: 'vendor' | 'pro';
  locked?: boolean;
};

const VENDOR_SURFACE_SECTIONS: VendorSectionConfig[] = [
  {
    id: 'home',
    label: 'Home',
    icon: 'layout-grid',
    to: vendorHub.home,
    minRole: 'viewer',
    surface: 'vendor',
    tabs: [
      { id: 'overview', label: 'Overview', to: vendorHub.home, minRole: 'viewer' },
      {
        id: 'analytics',
        label: 'Analytics',
        to: vendorHub.analytics,
        capability: 'shop',
        minRole: 'viewer',
        planFeature: 'analytics',
      },
    ],
  },
  {
    id: 'products',
    label: 'Products',
    icon: 'package',
    to: vendorHub.products,
    capability: 'shop',
    minRole: 'staff',
    surface: 'vendor',
    tabs: [
      { id: 'products', label: 'Products', to: vendorHub.products, capability: 'shop', minRole: 'staff' },
      {
        id: 'promotions',
        label: 'Promotions',
        to: vendorHub.promotions,
        capability: 'shop',
        minRole: 'manager',
        planFeature: 'promotions',
      },
      {
        id: 'flash-sales',
        label: 'Flash sales',
        to: vendorHub.flashSales,
        capability: 'shop',
        minRole: 'manager',
        planFeature: 'flashSales',
      },
      {
        id: 'bundles',
        label: 'Bundles',
        to: vendorHub.bundles,
        capability: 'shop',
        minRole: 'manager',
        planFeature: 'bundles',
      },
    ],
  },
  {
    id: 'orders',
    label: 'Orders',
    icon: 'shopping-bag',
    to: vendorHub.orders,
    capability: 'shop',
    minRole: 'staff',
    surface: 'vendor',
  },
  {
    id: 'bookings',
    label: 'Bookings',
    icon: 'clipboard-check',
    to: vendorHub.bookings,
    capability: 'bookings',
    minRole: 'viewer',
    surface: 'vendor',
  },
  {
    id: 'listings',
    label: 'Listings',
    icon: 'tag',
    to: vendorHub.classifieds,
    minRole: 'staff',
    surface: 'vendor',
    tabs: [
      {
        id: 'classifieds',
        label: 'Classifieds',
        to: vendorHub.classifieds,
        capability: 'classifieds',
        minRole: 'staff',
        planFeature: 'classifieds',
      },
      {
        id: 'properties',
        label: 'Properties',
        to: vendorHub.properties,
        capability: 'property',
        minRole: 'staff',
        planFeature: 'property_listings',
      },
      {
        id: 'property-inquiries',
        label: 'Viewing requests',
        to: vendorHub.propertyInquiries,
        capability: 'property',
        minRole: 'staff',
      },
    ],
  },
  {
    id: 'money',
    label: 'Money',
    icon: 'wallet',
    to: vendorHub.payouts,
    minRole: 'viewer',
    surface: 'vendor',
    tabs: [
      { id: 'payouts', label: 'Payouts', to: vendorHub.payouts, capability: 'payouts', minRole: 'owner' },
      { id: 'subscription', label: 'Subscription', to: vendorHub.subscription, minRole: 'owner' },
      {
        id: 'gift-cards',
        label: 'Gift certificates',
        to: vendorHub.giftCards,
        capability: 'shop',
        minRole: 'manager',
      },
    ],
  },
  {
    id: 'store',
    label: 'Store',
    icon: 'store',
    to: vendorHub.store,
    minRole: 'staff',
    surface: 'vendor',
    tabs: [
      { id: 'store', label: 'Store profile', to: vendorHub.store, minRole: 'staff' },
      { id: 'seo', label: 'SEO & visibility', to: vendorHub.seo, minRole: 'staff' },
      {
        id: 'boost',
        label: 'Ads & boost',
        to: vendorHub.boost,
        capability: 'shop',
        minRole: 'manager',
      },
    ],
  },
  {
    id: 'team',
    label: 'Team',
    icon: 'users',
    to: vendorHub.members,
    minRole: 'manager',
    surface: 'vendor',
    tabs: [
      { id: 'members', label: 'Members', to: vendorHub.members, minRole: 'manager' },
      {
        id: 'services',
        label: 'Team services',
        to: vendorHub.services,
        capability: 'bookings',
        minRole: 'staff',
      },
    ],
  },
];

const PRO_SURFACE_SECTIONS: VendorSectionConfig[] = [
  {
    id: 'home',
    label: 'Home',
    icon: 'layout-grid',
    to: proHub.home,
    minRole: 'viewer',
    surface: 'pro',
  },
  {
    id: 'bookings',
    label: 'Bookings',
    icon: 'clipboard-check',
    to: proHub.bookings,
    capability: 'bookings',
    minRole: 'viewer',
    surface: 'pro',
  },
  {
    id: 'profile',
    label: 'Profile',
    icon: 'store',
    to: proHub.profile,
    minRole: 'manager',
    surface: 'pro',
  },
  {
    id: 'team',
    label: 'Team',
    icon: 'users',
    to: vendorHub.members,
    minRole: 'manager',
    surface: 'pro',
    tabs: [
      { id: 'members', label: 'Members', to: vendorHub.members, minRole: 'manager' },
      {
        id: 'services',
        label: 'Team services',
        to: vendorHub.services,
        capability: 'bookings',
        minRole: 'staff',
      },
    ],
  },
];

const SELLER_CAPABILITIES: OrgCapability[] = ['shop', 'classifieds', 'property', 'payouts'];

export function orgHasSellerCapabilities(capabilities: OrgCapability[]): boolean {
  return capabilities.some((c) => SELLER_CAPABILITIES.includes(c));
}

function hasCap(capabilities: OrgCapability[], cap?: OrgCapability | null): boolean {
  return !cap || capabilities.includes(cap);
}

function filterSections(
  sections: VendorSectionConfig[],
  capabilities: OrgCapability[],
  memberRole: OrgMemberRole,
): VendorSectionConfig[] {
  return sections
    .filter((section) => !section.minRole || roleAtLeast(memberRole, section.minRole))
    .map((section) => {
      const sectionCapMissing = section.capability ? !hasCap(capabilities, section.capability) : false;

      const tabs = (section.tabs ?? [])
        .filter((tab) => !tab.minRole || roleAtLeast(memberRole, tab.minRole))
        .map((tab) => {
          const tabCap = tab.capability ?? section.capability;
          const capMissing = tabCap ? !hasCap(capabilities, tabCap) : sectionCapMissing;
          return { ...tab, locked: capMissing || tab.locked };
        });

      return {
        ...section,
        tabs: tabs.length > 0 ? tabs : section.tabs,
        locked: sectionCapMissing,
      };
    });
}

export type WorkspaceNavAccess = {
  hasVendorHub?: boolean;
  hasProHub?: boolean;
};

/** Flat sidebar sections for vendor/pro workspace.
 * Dual-role orgs get seller sections (includes Bookings) plus Pro profile when applicable.
 */
export function buildWorkspaceSections(
  capabilities: OrgCapability[],
  memberRole: OrgMemberRole,
  access?: WorkspaceNavAccess,
): VendorSectionConfig[] {
  const includeVendor =
    orgHasSellerCapabilities(capabilities) || Boolean(access?.hasVendorHub);
  const includePro = capabilities.includes('bookings') || Boolean(access?.hasProHub);

  if (includeVendor) {
    const sections = filterSections(VENDOR_SURFACE_SECTIONS, capabilities, memberRole);
    // Seller + Find a Pro: keep vendor nav (bookings lives under vendor) and add Pro profile.
    if (includePro && access?.hasProHub) {
      const profile = PRO_SURFACE_SECTIONS.find((s) => s.id === 'profile');
      if (profile && !sections.some((s) => s.id === 'profile')) {
        const [filteredProfile] = filterSections([profile], capabilities, memberRole);
        if (filteredProfile) sections.push(filteredProfile);
      }
    }
    return sections;
  }
  if (includePro) {
    return filterSections(PRO_SURFACE_SECTIONS, capabilities, memberRole);
  }
  return [];
}

export function getVendorSection(sectionId: VendorSectionId): VendorSectionConfig | undefined {
  return (
    VENDOR_SURFACE_SECTIONS.find((s) => s.id === sectionId) ??
    PRO_SURFACE_SECTIONS.find((s) => s.id === sectionId)
  );
}

export function vendorSectionForPath(pathname: string): VendorSectionId | null {
  if (pathname === vendorHub.home || pathname === vendorHub.analytics) return 'home';
  if (pathname === proHub.home) return 'home';
  if (
    pathname.startsWith(vendorHub.products) ||
    pathname.startsWith(vendorHub.promotions) ||
    pathname.startsWith(vendorHub.flashSales) ||
    pathname.startsWith(vendorHub.bundles)
  ) {
    return 'products';
  }
  if (pathname.startsWith(vendorHub.orders)) return 'orders';
  if (pathname.startsWith(vendorHub.bookings) || pathname.startsWith(proHub.bookings)) return 'bookings';
  if (
    pathname.startsWith(vendorHub.classifieds) ||
    pathname.startsWith(vendorHub.properties) ||
    pathname.startsWith(vendorHub.propertyInquiries)
  ) {
    return 'listings';
  }
  if (
    pathname.startsWith(vendorHub.payouts) ||
    pathname.startsWith(vendorHub.subscription) ||
    pathname.startsWith(vendorHub.giftCards)
  ) {
    return 'money';
  }
  if (
    pathname.startsWith(vendorHub.store) ||
    pathname.startsWith(vendorHub.seo) ||
    pathname.startsWith(vendorHub.boost)
  ) {
    return 'store';
  }
  if (pathname.startsWith(vendorHub.members) || pathname.startsWith(vendorHub.services)) return 'team';
  if (pathname.startsWith(proHub.profile)) return 'profile';
  return null;
}

export function vendorNavItemIsActive(pathname: string, section: VendorSectionConfig): boolean {
  const match = vendorSectionForPath(pathname);
  return match === section.id;
}

/** Hide section tab bar on full-page create/edit flows. */
export function vendorHubChromeHidden(pathname: string): boolean {
  return (
    pathname.includes('/new') ||
    pathname.includes('/edit') ||
    pathname.startsWith('/hub/admin')
  );
}

/** Flatten section tabs for command palette search. */
export function flatVendorDestinations(
  sections: VendorSectionConfig[] = VENDOR_SURFACE_SECTIONS,
): { label: string; to: string; section: string }[] {
  const rows: { label: string; to: string; section: string }[] = [];
  for (const section of sections) {
    if (!section.tabs?.length) {
      rows.push({ label: section.label, to: section.to, section: section.label });
      continue;
    }
    for (const tab of section.tabs) {
      rows.push({
        label: `${section.label} · ${tab.label}`,
        to: tab.to,
        section: section.label,
      });
    }
  }
  return rows;
}
